import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { useScopedSelect } from '@/contexts/ClientScopeContext';

/**
 * CIPP module (Microsoft 365 via CIPP) — types and hooks. Shapes mirror the DTOs
 * built in `server/cipp/`; the browser never sees a raw CIPP object.
 */

export interface CippTenant {
  /** Entra tenant id — the stable key. */
  customerId: string;
  displayName: string;
  defaultDomainName: string;
  initialDomainName: string | null;
  /** False when CIPP can't reach the tenant (broken GDAP / revoked consent). */
  healthy: boolean;
  graphErrorCount: number;
  delegatedPrivilegeStatus: string | null;
  lastRefresh: string | null;
  gdapExpires: string | null;
  gdapAutoExtend: boolean;
}

export interface CippTenantMapping {
  customerId: string;
  /** Autotask company id. `0` is a real id (Synfo's internal company). Null when ignored. */
  companyId: number | null;
  companyName: string | null;
  ignored: boolean;
  mappedBy: string;
  mappedAt: string;
}

export interface CippMatchReason {
  kind: 'contacts' | 'website' | 'name';
  domain?: string;
  count?: number;
}

export interface CippMatchCandidate {
  companyId: number;
  companyName: string;
  score: number;
  reasons: CippMatchReason[];
}

export interface CippTenantSuggestion {
  domains: string[];
  candidates: CippMatchCandidate[];
  /** Clear, well-evidenced winner — offered for bulk accept. */
  strong: boolean;
  error: string | null;
}

export interface CippSuggestJob {
  running: boolean;
  startedAt: string | null;
  finishedAt: string | null;
  done: number;
  total: number;
  error: string | null;
}

export interface CippTenantMapRow extends CippTenant {
  mapping: CippTenantMapping | null;
  suggestion: CippTenantSuggestion | null;
}

export interface CippTenantMapResponse {
  items: CippTenantMapRow[];
  job: CippSuggestJob;
  fetchedAt: string;
}

const MAP_KEY = ['cipp', 'tenant-map'] as const;

export function useCippTenantMap() {
  return useQuery({
    queryKey: MAP_KEY,
    queryFn: () => api.get<CippTenantMapResponse>('/api/cipp/tenant-map'),
    staleTime: 30_000,
    // Poll while the matching job is scoring tenants, so suggestions fill in live.
    refetchInterval: (q) => (q.state.data?.job.running ? 3_000 : false),
  });
}

export function useStartCippMatching() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ job: CippSuggestJob }>('/api/cipp/tenant-map/suggest', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: MAP_KEY }),
  });
}

export type CippMappingInput = { companyId: number; companyName: string } | { ignored: true };

export function useSetCippMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, value }: { customerId: string; value: CippMappingInput }) =>
      api.put(`/api/cipp/tenant-map/${customerId}`, value),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cipp'] }),
  });
}

export function useClearCippMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (customerId: string) => api.del(`/api/cipp/tenant-map/${customerId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cipp'] }),
  });
}

export function useAcceptCippMatches() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: { customerId: string; companyId: number; companyName: string }[]) =>
      api.post<{ saved: number }>('/api/cipp/tenant-map/accept', { items }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cipp'] }),
  });
}

// -------- Microsoft 365 card for an Autotask contact --------

export interface M365SignIn {
  at: string;
  app: string | null;
  success: boolean;
  failureReason: string | null;
  location: string | null;
  device: string | null;
  clientApp: string | null;
  ip: string | null;
}

export interface M365MailboxSummary {
  type: string | null;
  forwardingTo: string | null;
  forwardAndDeliver: boolean;
  holds: string[];
  hiddenFromGal: boolean;
  archive: boolean;
  sizeGb: number | null;
  quotaGb: number | null;
  itemCount: number | null;
  asOf: string | null;
}

export interface M365Core {
  /** no-tenant: the contact's company has no mapped tenant. not-found: no M365 user with that email. */
  state: 'ok' | 'no-tenant' | 'no-email' | 'not-found';
  email: string | null;
  tenant: { customerId: string; displayName: string; defaultDomainName: string } | null;
  user: {
    id: string;
    displayName: string;
    upn: string;
    accountEnabled: boolean;
    userType: string | null;
    jobTitle: string | null;
    department: string | null;
    licences: string[];
    createdDateTime: string | null;
    syncedFromOnPrem: boolean;
    cippUrl: string;
  } | null;
  mfa: {
    /** null = unknown (CIPP's report has no value). */
    registered: boolean | null;
    capable: boolean | null;
    methods: string[];
    perUser: string | null;
    coveredByCA: string | null;
    coveredBySecurityDefaults: boolean | null;
    isAdmin: boolean;
    asOf: string | null;
  } | null;
  mailbox: M365MailboxSummary | null;
  signIns: M365SignIn[];
  errors: { mfa?: string; mailbox?: string; signIns?: string };
  fetchedAt: string;
}

export interface M365OutOfOffice {
  ooo: { state: string; start: string | null; end: string | null; message: string | null } | null;
  error: string | null;
  fetchedAt: string;
}

/** CIPP calls take seconds — keep results for the server's cache window and don't refetch on focus. */
const M365_STALE = 5 * 60_000;

export function useContactM365(contactId: number | null | undefined) {
  return useQuery({
    queryKey: ['cipp', 'contact-m365', contactId],
    queryFn: () => api.get<M365Core>(`/api/cipp/contacts/${contactId}/m365`),
    enabled: !!contactId,
    staleTime: M365_STALE,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useContactM365OutOfOffice(contactId: number | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['cipp', 'contact-m365-ooo', contactId],
    queryFn: () => api.get<M365OutOfOffice>(`/api/cipp/contacts/${contactId}/m365/ooo`),
    enabled: !!contactId && enabled,
    staleTime: M365_STALE,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/** Bypass both caches (server + client) for one contact. */
export function useRefreshContactM365() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (contactId: number) => {
      const [core, ooo] = await Promise.all([
        api.get<M365Core>(`/api/cipp/contacts/${contactId}/m365?fresh=1`),
        api.get<M365OutOfOffice>(`/api/cipp/contacts/${contactId}/m365/ooo?fresh=1`),
      ]);
      qc.setQueryData(['cipp', 'contact-m365', contactId], core);
      qc.setQueryData(['cipp', 'contact-m365-ooo', contactId], ooo);
    },
  });
}

// -------- Tenants module pages --------

export interface CippTenantListRow extends CippTenant {
  mapping: CippTenantMapping | null;
}

/** `scoped`: narrow to the client picked in the header (ClientScopeContext). */
export function useCippTenants(opts: { scoped?: boolean } = {}) {
  const select = useScopedSelect<{ items: CippTenantListRow[]; fetchedAt: string }>(opts.scoped, (d, inScope) => ({ ...d, items: d.items.filter((x) => inScope(x.mapping?.companyId)) }));
  return useQuery({
    select,
    queryKey: ['cipp', 'tenants'],
    queryFn: () => api.get<{ items: CippTenantListRow[]; fetchedAt: string }>('/api/cipp/tenants'),
    staleTime: 5 * 60_000,
  });
}

export interface CippTenantUser {
  id: string | null;
  upn: string;
  displayName: string;
  enabled: boolean;
  licensed: boolean;
  userType: string | null;
  isAdmin: boolean;
  /** null = unknown (CIPP's report has no value). */
  mfaRegistered: boolean | null;
  mfaMethods: string[];
  mfaEnforcedBy: 'Security defaults' | 'Conditional access' | 'Per-user' | null;
  mailbox: { type: string | null; sizeGb: number | null; quotaGb: number | null; forwardingTo: string | null; litigationHold: boolean } | null;
  cippUrl: string | null;
}

export interface CippTenantDetail {
  tenant: CippTenantListRow;
  users: CippTenantUser[];
  asOf: { mfa: string | null; mailboxes: string | null };
  errors: { mfa?: string; mailboxes?: string };
  cippUrl: string;
}

export function useCippTenant(customerId: string | undefined) {
  return useQuery({
    queryKey: ['cipp', 'tenant', customerId],
    queryFn: () => api.get<CippTenantDetail>(`/api/cipp/tenants/${customerId}`),
    enabled: !!customerId,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

// -------- Intune devices (cross-linked to Datto RMM) --------

export interface IntuneRmmLink {
  uid: string;
  hostname: string;
  online: boolean;
  lastSeen: string | null;
  matchedBy: 'serial' | 'mac' | 'hostname';
  confidence: 'high' | 'medium';
  /** The Datto site's Autotask company differs from the tenant's mapped company. */
  companyMismatch: boolean;
}

export interface IntuneDevice {
  id: string;
  deviceName: string;
  serialNumber: string | null;
  manufacturer: string | null;
  model: string | null;
  os: string | null;
  osVersion: string | null;
  /** compliant | noncompliant | inGracePeriod | unknown | … */
  compliance: string | null;
  lastSync: string | null;
  enrolled: string | null;
  ownership: 'company' | 'personal' | null;
  managementAgent: string | null;
  userPrincipalName: string | null;
  userDisplayName: string | null;
  encrypted: boolean | null;
  autopilot: boolean;
  tenant: { customerId: string; displayName: string; defaultDomainName: string };
  companyId: number | null;
  companyName: string | null;
  rmm: IntuneRmmLink | null;
}

export interface IntuneDeviceList {
  items: IntuneDevice[];
  fetchedAt: string;
  tenants: { total: number; unlicensed: number; failed: { customerId: string; displayName: string; error: string }[] };
}

/** All tenants' Intune devices. The server caches 30 min (a cold load is ~10–30 s), so don't poll. */
/** `scoped`: narrow to the client picked in the header (ClientScopeContext). */
export function useIntuneDevices(opts: { scoped?: boolean } = {}) {
  const select = useScopedSelect<IntuneDeviceList>(opts.scoped, (d, inScope) => ({ ...d, items: d.items.filter((x) => inScope(x.companyId)) }));
  return useQuery({
    select,
    queryKey: ['cipp', 'intune-devices'],
    queryFn: () => api.get<IntuneDeviceList>('/api/cipp/devices'),
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false,
  });
}

/** The Intune record(s) matched to a Datto RMM device. */
export function useIntuneForRmmDevice(uid: string | undefined) {
  return useQuery({
    queryKey: ['cipp', 'intune-for-rmm', uid],
    queryFn: () => api.get<{ items: IntuneDevice[]; fetchedAt: string }>(`/api/cipp/devices/rmm/${uid}`),
    enabled: !!uid,
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

// -------- Security posture (mirrors server/cipp/security.ts) --------

export type PostureSeverity = 'critical' | 'warning' | 'info';
export type CheckStatus = 'pass' | 'warn' | 'fail';
export type MfaEnforcementSummary = 'Security defaults' | 'Conditional access' | 'Per-user' | 'Mixed' | 'Partial' | 'None';

export interface TenantPosture {
  tenant: { customerId: string; displayName: string; defaultDomainName: string };
  companyId: number | null;
  companyName: string | null;
  secureScore: { pct: number; current: number; max: number; capturedAt: string | null } | null;
  mfa: {
    /** Enabled, licensed, non-guest accounts. */
    members: number;
    registered: number;
    notRegistered: number;
    /** No registration data — tenants without Entra P1. */
    unknown: number;
    admins: number;
    adminsNotRegistered: number;
    /** Members no policy enforces MFA for. */
    unprotected: number;
    enforcement: MfaEnforcementSummary | null;
    asOf: string | null;
  } | null;
  ca: {
    enabled: number;
    reportOnly: number;
    disabled: number;
    mfaForAll: boolean;
    blocksLegacyAuth: boolean;
    policies: { name: string; state: 'enabled' | 'reportOnly' | 'disabled'; controls: string[] }[];
  } | null;
  forwarding: { smtp: number; internal: number; mailboxes: { upn: string; to: string; external: boolean }[] } | null;
  alignment: { score: number; templates: { name: string; score: number; combined: number; licenceMissing: number }[]; collectedAt: string | null } | null;
  /** From CIPP's daily Domain Analyser — one mail domain per tenant. */
  mail: {
    domain: string;
    dmarc: 'missing' | 'none' | 'quarantine' | 'reject' | null;
    spf: CheckStatus | null;
    dkim: CheckStatus | null;
    scorePct: number | null;
    mailProvider: string | null;
    notes: string[];
    /** DKIM CNAME targets to publish, when Exchange has them. */
    dkimFix: { host: string; value: string }[] | null;
    checkedAt: string | null;
    otherDomains: number;
  } | null;
  issues: { id: string; severity: PostureSeverity; label: string }[];
  errors: Record<string, string>;
}

export interface SecurityTrendPoint {
  day: string;
  secureScorePct: number | null;
  mfaPct: number | null;
  unprotected: number;
  adminsNoMfa: number;
}

export interface SecurityPostureResponse {
  building: boolean;
  progress: { done: number; total: number };
  fetchedAt: string | null;
  mailFetchedAt: string | null;
  mailBuilding: boolean;
  items: TenantPosture[];
  alignmentError: string | null;
  trend: SecurityTrendPoint[];
}

/** Served from server-side background caches; polls only while the first load is building. */
/** `scoped`: narrow to the client picked in the header (ClientScopeContext). */
export function useSecurityPosture(opts: { scoped?: boolean } = {}) {
  const select = useScopedSelect<SecurityPostureResponse>(opts.scoped, (d, inScope) => ({ ...d, items: d.items.filter((x) => inScope(x.companyId)) }));
  return useQuery({
    select,
    queryKey: ['cipp', 'security'],
    queryFn: () => api.get<SecurityPostureResponse>('/api/cipp/security'),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    refetchInterval: (q) => (q.state.data?.building || q.state.data?.mailBuilding ? 5_000 : false),
  });
}

export function useTenantPosture(customerId: string | undefined) {
  return useQuery({
    queryKey: ['cipp', 'security', customerId],
    queryFn: () => api.get<{ posture: TenantPosture | null; building: boolean; fetchedAt: string | null; trend: SecurityTrendPoint[] }>(`/api/cipp/security/${customerId}`),
    enabled: !!customerId,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    refetchInterval: (q) => (q.state.data?.building ? 5_000 : false),
  });
}
